import { isBrowser } from "@/utils/is-browser.utils";
import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

type Locale = "en" | "fr";

type SeoProps = {
  /** Plain text title (no site name). Example: "Privacy Policy" */
  title: string;

  /** Meta description */
  description?: string;

  /**
   * Canonical pathname for the *EN unprefixed* version.
   * Example: "/privacy/" or "/" or "/home/"
   *
   * We will derive FR as "/fr" + canonicalPath.
   */
  canonicalPath?: string;

  /** If true, adds robots noindex/nofollow */
  noindex?: boolean;

  /** Optional OG image absolute URL */
  ogImage?: string;

  /**
   * Public site origin (recommended via env).
   * Example: "https://stratistics.app"
   */
  siteUrl?: string;

  /** Optional site name shown in <title> suffix and og:site_name */
  siteName?: string;
};

const DEFAULT_SITE_NAME = "Stratistics";

function ensureLeadingSlash(p: string) {
  return p.startsWith("/") ? p : `/${p}`;
}

function ensureTrailingSlash(p: string) {
  return p.endsWith("/") ? p : `${p}/`;
}

/**
 * - EN is unprefixed (canonical)
 * - FR is "/fr" + EN path
 */
function buildLocalePaths(enCanonicalPath: string) {
  const en = ensureTrailingSlash(ensureLeadingSlash(enCanonicalPath));
  const fr = en === "/" ? "/fr/" : ensureTrailingSlash(`/fr${en}`);
  return { en, fr };
}

function detectLocaleFromPathname(pathname: string): Locale {
  if (pathname === "/fr" || pathname.startsWith("/fr/")) return "fr";
  return "en";
}

function joinUrl(origin: string, pathname: string) {
  const o = origin.replace(/\/+$/, "");
  const p = ensureLeadingSlash(pathname);
  return `${o}${p}`;
}

function removeOwnedTags(owner: string) {
  document
    .querySelectorAll(`[data-seo-owner="${owner}"]`)
    .forEach((el) => el.remove());
}

function upsertMetaByName(owner: string, name: string, content: string) {
  const selector = `meta[name="${CSS.escape(name)}"][data-seo-owner="${owner}"]`;
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  const el = existing ?? document.createElement("meta");
  el.setAttribute("name", name);
  el.setAttribute("content", content);
  el.setAttribute("data-seo-owner", owner);
  if (!existing) document.head.appendChild(el);
}

function upsertMetaByProperty(
  owner: string,
  property: string,
  content: string,
) {
  const selector = `meta[property="${CSS.escape(property)}"][data-seo-owner="${owner}"]`;
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  const el = existing ?? document.createElement("meta");
  el.setAttribute("property", property);
  el.setAttribute("content", content);
  el.setAttribute("data-seo-owner", owner);
  if (!existing) document.head.appendChild(el);
}

function upsertLink(
  owner: string,
  rel: string,
  href: string,
  extra?: Record<string, string>,
) {
  const key = extra?.hreflang ? `|hreflang=${extra.hreflang}` : "";
  const selector = `link[rel="${CSS.escape(rel)}"][data-seo-owner="${owner}"][data-seo-key="${CSS.escape(
    rel + key,
  )}"]`;
  const existing = document.head.querySelector<HTMLLinkElement>(selector);
  const el = existing ?? document.createElement("link");
  el.setAttribute("rel", rel);
  el.setAttribute("href", href);
  el.setAttribute("data-seo-owner", owner);
  el.setAttribute("data-seo-key", rel + key);
  if (extra) {
    for (const [k, v] of Object.entries(extra)) el.setAttribute(k, v);
  }
  if (!existing) document.head.appendChild(el);
}

export function Seo(props: SeoProps) {
  const {
    title,
    description,
    canonicalPath,
    noindex,
    ogImage,
    siteUrl = import.meta.env.VITE_SITE_URL ?? "",
    siteName = DEFAULT_SITE_NAME,
  } = props;

  const location = useLocation();

  useLayoutEffect(() => {
    if (!isBrowser) return;

    // VITE_SITE_URL in Netlify env for correct absolute URLs.
    // If missing, we fallback to window.origin (works in browser & prerender).
    const origin = siteUrl?.startsWith("http")
      ? siteUrl
      : window.location.origin;

    // If canonicalPath isn't provided, use the current path (normalized)
    const fallbackPath = location.pathname.endsWith("/")
      ? location.pathname
      : `${location.pathname}/`;

    // Single owner is fine if only one Seo mounted per page
    const owner = "seo";
    removeOwnedTags(owner);

    const currentLocale = detectLocaleFromPathname(location.pathname);
    const enCanonical = canonicalPath ?? fallbackPath;
    const { en: enPath, fr: frPath } = buildLocalePaths(enCanonical);

    // Canonical: depends on locale
    const canonicalAbs =
      currentLocale === "fr"
        ? joinUrl(origin, frPath)
        : joinUrl(origin, enPath);

    // Alternates
    const enAbs = joinUrl(origin, enPath);
    const frAbs = joinUrl(origin, frPath);

    // <html lang="">
    document.documentElement.lang = currentLocale;

    // Title
    document.title = `${title} · ${siteName}`;

    // Basic meta
    if (description) upsertMetaByName(owner, "description", description);

    // Robots
    if (noindex) {
      upsertMetaByName(owner, "robots", "noindex,nofollow,noarchive");
    } else {
      // Leaving it explicit is also fine.
      upsertMetaByName(owner, "robots", "index,follow");
    }

    // Canonical link
    upsertLink(owner, "canonical", canonicalAbs);

    // hreflang
    upsertLink(owner, "alternate", enAbs, { hreflang: "en" });
    upsertLink(owner, "alternate", frAbs, { hreflang: "fr" });
    upsertLink(owner, "alternate", enAbs, { hreflang: "x-default" });

    // Open Graph
    upsertMetaByProperty(owner, "og:site_name", siteName);
    upsertMetaByProperty(owner, "og:title", title);
    if (description) upsertMetaByProperty(owner, "og:description", description);
    upsertMetaByProperty(owner, "og:type", "website");
    upsertMetaByProperty(owner, "og:url", canonicalAbs);
    upsertMetaByProperty(
      owner,
      "og:locale",
      currentLocale === "fr" ? "fr_FR" : "en_US",
    );
    upsertMetaByProperty(
      owner,
      "og:locale:alternate",
      currentLocale === "fr" ? "en_US" : "fr_FR",
    );
    if (ogImage) upsertMetaByProperty(owner, "og:image", ogImage);

    // Twitter
    upsertMetaByName(
      owner,
      "twitter:card",
      ogImage ? "summary_large_image" : "summary",
    );
    upsertMetaByName(owner, "twitter:title", title);
    if (description)
      upsertMetaByName(owner, "twitter:description", description);
    if (ogImage) upsertMetaByName(owner, "twitter:image", ogImage);

    return () => {
      removeOwnedTags(owner);
    };
  }, [
    title,
    description,
    canonicalPath,
    noindex,
    ogImage,
    siteUrl,
    siteName,
    location.pathname,
  ]);

  return null;
}
